import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X, SlidersHorizontal } from "lucide-react";
import { Widget } from "./Widgets";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const SPAN = { 1: "md:col-span-1", 2: "md:col-span-2", 3: "md:col-span-3" };
const RANGE_LABELS = { "7d": "7 days", "30d": "30 days", "90d": "90 days", "180d": "180 days", "360d": "360 days", "720d": "720 days", "1080d": "1080 days" };
const CHART_TYPES = ["line", "area", "bar"];

function WidgetConfig({ widget, options, onConfigure }) {
  const f = widget.filters || {};
  const set = (k, v) => {
    const next = { ...f };
    if (v === "__global") delete next[k];
    else next[k] = v;
    onConfigure(widget.id, Object.keys(next).length ? next : null);
  };
  const showRegionTeam = widget.type !== "bar";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-6 w-6 flex items-center justify-center rounded bg-secondary text-foreground border border-border hover:bg-accent transition-colors" data-testid={`config-widget-${widget.id}`} title="Widget filters">
          <SlidersHorizontal className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 space-y-3" data-testid={`config-popover-${widget.id}`}>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Widget filters</p>
        <div className="space-y-1">
          <Label className="text-xs">Date range</Label>
          <Select value={f.range || "__global"} onValueChange={(v) => set("range", v)}>
            <SelectTrigger className="h-8" data-testid={`config-range-${widget.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__global">Follow global</SelectItem>
              {(options.ranges || []).map((r) => <SelectItem key={r} value={r}>{RANGE_LABELS[r] || r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {showRegionTeam && (
          <>
            <div className="space-y-1">
              <Label className="text-xs">Region</Label>
              <Select value={f.region || "__global"} onValueChange={(v) => set("region", v)}>
                <SelectTrigger className="h-8" data-testid={`config-region-${widget.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__global">Follow global</SelectItem>
                  <SelectItem value="all">All regions</SelectItem>
                  {(options.regions || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Team</Label>
              <Select value={f.team || "__global"} onValueChange={(v) => set("team", v)}>
                <SelectTrigger className="h-8" data-testid={`config-team-${widget.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__global">Follow global</SelectItem>
                  <SelectItem value="all">All teams</SelectItem>
                  {(options.teams || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {widget.filters && (
          <Button variant="outline" size="sm" className="w-full" onClick={() => onConfigure(widget.id, null)} data-testid={`config-clear-${widget.id}`}>
            Clear overrides
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function SortableWidget({ widget, editMode, onRemove, onConfigure, options = {}, ...rest }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: widget.id, disabled: !editMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };

  const span = widget.type === "kpi" ? "md:col-span-1" : SPAN[widget.span] || "md:col-span-1";
  const configurable = CHART_TYPES.includes(widget.type);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative col-span-1 ${span} ${editMode ? "ring-1 ring-primary/40 rounded-md" : ""}`}
      data-testid={`widget-${widget.id}`}
    >
      {editMode && (
        <div className="absolute -top-2.5 right-2 z-10 flex gap-1">
          {configurable && onConfigure && <WidgetConfig widget={widget} options={options} onConfigure={onConfigure} />}
          <button
            {...attributes}
            {...listeners}
            className="h-6 w-6 flex items-center justify-center rounded bg-primary text-primary-foreground cursor-grab active:cursor-grabbing"
            data-testid={`drag-handle-${widget.id}`}
            title="Drag to reorder"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onRemove(widget.id)}
            className="h-6 w-6 flex items-center justify-center rounded bg-destructive text-destructive-foreground hover:opacity-90 transition-opacity"
            data-testid={`remove-widget-${widget.id}`}
            title="Remove widget"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <Widget widget={widget} {...rest} />
    </div>
  );
}
